import React, { PropsWithChildren } from 'react';
import { FormInputElement } from '@typedream/components';
import {
  DreamValue,
  FormInputElement as FormInputElementType,
  FormInputElementWithOptions,
  FormInputOption,
  FormInputTypesWithOptions,
} from '@typedream/core';
import { findNodePath, PlateRenderElementProps, setNodes } from '@udecode/plate-core';
import { produce } from 'immer';
import { pick } from 'lodash';
import styled from 'styled-components';
import { CaretDown, CaretUp } from '@styled-icons/boxicons-regular';
import { Trash } from '@styled-icons/feather';
import { useIFrame } from '../../../components/Iframe';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import { DreamEditor } from '../../../types';
import { useTPlateEditorRef } from '../../../plate';

interface BaseProps {
  // eslint-disable-next-line react/require-default-props
  className?: string;
  [key: string]: unknown;
}

const ButtonAccent = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-radius: 8px;
  padding: 10px 15px !important;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
  height: fit-content;
  line-height: 21px;
  position: relative;

  border: none;
  background: var(--accent);
  color: var(--text-on-accent);
`;

const ButtonWithBorder = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-radius: 8px;
  padding: 10px 15px !important;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
  height: fit-content;
  line-height: 21px;
  position: relative;

  background: none;
  border: 1px solid var(--border-modal);
  color: var(--text-lighter);

  border: 1px solid var(--accent);
  color: var(--accent);

  &:hover {
    background-color: var(--accent-light);
    border: 1px solid var(--accent);
    color: var(--accent);
  }
`;

function ButtonAccentBorder({
  className,
  onClick,
  children,
  disabled,
  loading,
  small,
  ...props
}: PropsWithChildren<{
  onClick?: any;
  disabled?: boolean;
  loading?: boolean;
  small?: boolean;
}> &
  BaseProps) {
  return (
    <ButtonWithBorder
      type="button"
      onClick={disabled || loading ? () => {} : onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </ButtonWithBorder>
  );
}

const FormInputActions = styled.div`
  padding: 10px 5px;
  display: flex;
  justify-content: space-between;
  user-select: none;
`;

const FormInputEditActionButton = styled.div`
  color: var(--accent);
  cursor: pointer;
`;

const FormInputEditOptionLabelContainer = styled.div`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid var(--neutral-200);
  background: var(--neutral-100);
  margin: 5px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  & > input {
    all: unset;
    width: 100%;
    font-size: 16px;
  }

  & > svg {
    transition: opacity 0.2s;
    opacity: 0;
  }

  &:hover > svg {
    opacity: 1;
  }
`;

ButtonAccentBorder.defaultProps = {
  disabled: false,
  loading: false,
  small: false,
  onClick: () => {},
};

function isFormInputTypeWithOptions(s: FormInputElementType): s is FormInputElementWithOptions {
  // if s.inputType is included in FormInputTypesWithOptions, return true
  // else return false
  return (FormInputTypesWithOptions as readonly string[]).includes(s.inputType);
}

function FormInputElementWithOptionsEditor(
  props: PlateRenderElementProps<DreamValue, FormInputElementWithOptions>
) {
  const { window: canvasWindow } = useIFrame();
  const { element, children } = props;

  const editor = useTPlateEditorRef();
  const [isEditing, setIsEditing] = React.useState(false);

  // Can this be Partial<FormInputOption> while maintaining high degree of type safety later?
  const [currentOptions, setCurrentOptions] = React.useState<Partial<FormInputOption>[]>([]);

  React.useEffect(() => {
    if (isEditing) return;
    setCurrentOptions(element.options || []);
  }, [element]);

  const handleSaveOptions = () => {
    const entry = getSelectedElementEntry(editor as unknown as DreamEditor);
    if (!entry) return;
    const [, path] = entry;

    setNodes(
      editor,
      {
        options: currentOptions,
      },
      {
        at: path,
      }
    );
  };

  return (
    <FormInputElement {...pick(props, ['element', 'className', 'attributes'])}>
      {children}
      {isEditing && (
        <div contentEditable={false}>
          {currentOptions.map((val, idx) => (
            <FormInputEditOptionLabelContainer>
              <input
                type="text"
                value={currentOptions[idx].label}
                onChange={(e) =>
                  setCurrentOptions((currVal) =>
                    produce(currVal, (draft) => {
                      draft[idx].label = e.target.value;
                    })
                  )
                }
                onKeyDown={(e) => {
                  // handling deleting empty option.
                  if (e.code === 'Backspace' && e.currentTarget.value === '') {
                    setCurrentOptions((currVal) => [
                      ...currVal.slice(0, idx),
                      ...currVal.slice(idx + 1, currVal.length),
                    ]);
                  }
                }}
              />

              {/* TODO: Implement drag and drop */}
              <CaretUp
                size={20}
                style={{ cursor: 'pointer', color: 'var(--color-text)' }}
                onClick={() => {
                  setCurrentOptions((currVal) => {
                    const v = [...currVal];
                    if (idx === 0) {
                      // put this element at the last
                      canvasWindow?.scrollBy(0, 50 * (v.length - 1));
                      const tmp = v[idx];
                      v.splice(idx, 1);
                      v.push(tmp);
                      return v;
                    }
                    canvasWindow?.scrollBy(0, -50);
                    const tmp = v[idx];
                    v[idx] = v[idx - 1];
                    v[idx - 1] = tmp;
                    return v;
                  });
                }}
              />
              <CaretDown
                size={20}
                style={{ cursor: 'pointer', color: 'var(--color-text)' }}
                onClick={() => {
                  // scroll window down based on container element height
                  // TODO: Dynamic height

                  // move down
                  setCurrentOptions((currVal) => {
                    const v = [...currVal];
                    if (idx === v.length - 1) {
                      // put this element at the first
                      const tmp = v[idx];
                      v.splice(idx, 1);
                      v.unshift(tmp);
                      canvasWindow?.scrollBy(0, -50 * (v.length - 1));
                      return v;
                    }
                    canvasWindow?.scrollBy(0, 50);
                    const tmp = v[idx];
                    v[idx] = v[idx + 1];
                    v[idx + 1] = tmp;
                    return v;
                  });
                }}
              />

              <Trash
                size={20}
                style={{ cursor: 'pointer', color: 'var(--color-text)' }}
                onClick={() => {
                  setCurrentOptions((currVal) => [
                    ...currVal.slice(0, idx),
                    ...currVal.slice(idx + 1, currVal.length),
                  ]);
                }}
              />
            </FormInputEditOptionLabelContainer>
          ))}
          <ButtonAccentBorder
            style={{ width: '100%', margin: '10px 0px' }}
            onClick={() => setCurrentOptions((currOpts) => [...currOpts, { label: 'New Option' }])}
          >
            Add new option
          </ButtonAccentBorder>
        </div>
      )}

      {isEditing && (
        <ButtonAccent
          style={{ width: '100%' }}
          onClick={() => {
            handleSaveOptions();
            setIsEditing(false);
          }}
        >
          Save options
        </ButtonAccent>
      )}
      {!isEditing && (
        <FormInputActions contentEditable={false}>
          <FormInputEditActionButton role="none" onClick={() => setIsEditing(true)}>
            <u>Edit options</u>
          </FormInputEditActionButton>
          <div className="with-lighter-text-color">{currentOptions.length} options in the list</div>
        </FormInputActions>
      )}
    </FormInputElement>
  );
}

const StyledLabel = styled.label`
  margin-bottom: 4px;
  font-size: 0.8rem;
`;

function FormInputMultiSelectionEditor(
  props: PlateRenderElementProps<DreamValue, FormInputElementWithOptions>
) {
  const { window: canvasWindow } = useIFrame();
  const { element, children, attributes } = props;
  const [isEditing, setIsEditing] = React.useState(false);

  const editor = useTPlateEditorRef();
  // Can this be Partial<FormInputOption> while maintaining high degree of type safety later?
  const [currentOptions, setCurrentOptions] = React.useState<Partial<FormInputOption>[]>([]);

  React.useEffect(() => {
    if (isEditing) return;
    setCurrentOptions(element.options || []);
  }, [element]);

  const p = React.useMemo(() => findNodePath(editor, element), [editor, element]);

  const handleSaveOptions = () => {
    setNodes(
      editor,
      {
        options: currentOptions,
      },
      {
        at: p,
      }
    );
  };

  if (isEditing) {
    return (
      <div
        style={{
          width: '100%',
          userSelect: 'none',
        }}
        contentEditable={false}
        {...((attributes as any) || {})}
      >
        {!element.is_label_hidden && <StyledLabel>{element.label}</StyledLabel>}
        {currentOptions.map((val, idx) => (
          <FormInputEditOptionLabelContainer>
            <input
              type="text"
              value={currentOptions[idx].label}
              onChange={(e) =>
                setCurrentOptions((currVal) =>
                  produce(currVal, (draft) => {
                    draft[idx].label = e.target.value;
                  })
                )
              }
              onKeyDown={(e) => {
                // handling deleting empty option.
                if (e.code === 'Backspace' && e.currentTarget.value === '') {
                  setCurrentOptions((currVal) => [
                    ...currVal.slice(0, idx),
                    ...currVal.slice(idx + 1, currVal.length),
                  ]);
                }
              }}
            />

            {/* TODO: Implement drag and drop */}
            <CaretUp
              size={20}
              style={{ cursor: 'pointer', color: 'var(--color-text)' }}
              onClick={() => {
                setCurrentOptions((currVal) => {
                  const v = [...currVal];
                  if (idx === 0) {
                    // put this element at the last
                    canvasWindow?.scrollBy(0, 50 * (v.length - 1));
                    const tmp = v[idx];
                    v.splice(idx, 1);
                    v.push(tmp);
                    return v;
                  }
                  canvasWindow?.scrollBy(0, -50);
                  const tmp = v[idx];
                  v[idx] = v[idx - 1];
                  v[idx - 1] = tmp;
                  return v;
                });
              }}
            />
            <CaretDown
              size={20}
              style={{ cursor: 'pointer', color: 'var(--color-text)' }}
              onClick={() => {
                // scroll window down based on container element height
                // TODO: Dynamic height

                // move down
                setCurrentOptions((currVal) => {
                  const v = [...currVal];
                  if (idx === v.length - 1) {
                    // put this element at the first
                    const tmp = v[idx];
                    v.splice(idx, 1);
                    v.unshift(tmp);
                    canvasWindow?.scrollBy(0, -50 * (v.length - 1));
                    return v;
                  }
                  canvasWindow?.scrollBy(0, 50);
                  const tmp = v[idx];
                  v[idx] = v[idx + 1];
                  v[idx + 1] = tmp;
                  return v;
                });
              }}
            />
            <Trash
              size={20}
              style={{ cursor: 'pointer', color: 'var(--color-text)' }}
              onClick={() => {
                setCurrentOptions((currVal) => [
                  ...currVal.slice(0, idx),
                  ...currVal.slice(idx + 1, currVal.length),
                ]);
              }}
            />
          </FormInputEditOptionLabelContainer>
        ))}
        <ButtonAccentBorder
          style={{ width: '100%', margin: '10px 0px' }}
          onClick={() => setCurrentOptions((currOpts) => [...currOpts, { label: 'New Option' }])}
        >
          Add new option
        </ButtonAccentBorder>
        <ButtonAccent
          style={{ width: '100%' }}
          onClick={() => {
            handleSaveOptions();
            setIsEditing(false);
          }}
        >
          Save options
        </ButtonAccent>
      </div>
    );
  }

  return (
    <FormInputElement {...pick(props, ['element', 'className', 'attributes'])}>
      {children}
      <FormInputEditActionButton role="none" onClick={() => setIsEditing(true)}>
        <u>Edit options</u>
      </FormInputEditActionButton>
    </FormInputElement>
  );
}

export function FormInputElementEditor(
  props: PlateRenderElementProps<DreamValue, FormInputElementType>
) {
  const { element, children } = props;

  // disable element editor for now
  const disabledElement = { ...element, disabled: true };

  if (!isFormInputTypeWithOptions(disabledElement)) {
    return (
      <FormInputElement element={disabledElement} {...pick(props, ['className', 'attributes'])}>
        {children}
      </FormInputElement>
    );
  }

  if (disabledElement.inputType === 'selection') {
    return <FormInputElementWithOptionsEditor {...props} element={disabledElement} />;
  }

  return <FormInputMultiSelectionEditor {...props} element={disabledElement} />;
}
